import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { pathFor } from "../app/paths";
import { dialable } from "../lib/dialable";

/**
 * Impressum — le indicazioni obbligatorie previste dal § 5 TMG.
 *
 * **Perché i dati stanno nelle traduzioni e non in una costante.**
 * Un Impressum è un documento legale: ciò che vi compare deve poter essere
 * corretto da chi risponde di quel contenuto, non da chi tocca il codice. Le
 * traduzioni sono già il posto dove vivono i testi del sito, quindi è lì che
 * deve stare anche questo — comprese le voci che non cambiano fra una lingua e
 * l'altra.
 *
 * **Cosa si traduce e cosa no.** Le etichette sì: «Geschäftsführer» diventa
 * «Amministratori» e «Managing Directors». I dati no: una partita IVA, un
 * numero di registro e un indirizzo sono identificativi, e tradurli
 * significherebbe indicare un'altra azienda. L'unica eccezione è il nome del
 * paese, che è una parola e non un identificativo.
 *
 * La struttura visiva riprende quella della Privacy: stesse variabili di
 * colore e tipografia, stessa griglia etichetta/valore che su schermo stretto
 * collassa su una colonna.
 */

const IMPRESSUM_CSS = `
.im-overline { font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: var(--color-accent); margin-bottom: 18px; }
.im-body { color: rgba(28,75,59,0.86); font-family: var(--font-body); font-size: 15px; line-height: 1.85; }
.im-body h1 { font-family: var(--font-heading); font-weight: 400; font-size: clamp(28px, 5vw, 44px); color: var(--color-dark); line-height: 1.15; letter-spacing: -0.5px; margin: 0 0 12px; }
.im-sub { font-size: 14px; color: var(--color-muted); margin: 0 0 40px; font-style: italic; }
.im-company { font-family: var(--font-heading); font-size: clamp(20px, 3.4vw, 27px); color: var(--color-dark); font-weight: 400; margin: 0 0 4px; }
.im-address { margin: 0 0 40px; white-space: pre-line; }
.im-field { display: grid; grid-template-columns: 220px 1fr; gap: 16px; padding: 14px 0; border-top: 1px solid rgba(28,75,59,0.10); }
.im-label { font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: var(--color-muted); padding-top: 3px; }
.im-value { margin: 0; }
.im-value a { color: var(--color-dark); text-decoration: none; border-bottom: 1px solid rgba(28,75,59,0.28); transition: border-color 0.2s; }
.im-value a:hover { border-bottom-color: var(--color-dark); }
.im-disclaimer { margin-top: 48px; padding-top: 26px; border-top: 1px solid rgba(28,75,59,0.10); }
.im-disclaimer h2 { font-family: var(--font-heading); font-weight: 400; font-size: clamp(20px, 3.4vw, 27px); color: var(--color-dark); line-height: 1.25; letter-spacing: -0.3px; margin: 0 0 16px; }
.im-disclaimer p { margin: 0; }
.im-back { display: inline-block; margin-top: 48px; color: var(--color-muted); font-size: 13px; font-weight: 500; text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
.im-back:hover { color: var(--color-dark); }
@media (max-width: 640px) {
  .im-field { grid-template-columns: 1fr; gap: 2px; padding: 12px 0; }
  .im-label { padding-top: 6px; }
}
`;


/** Righe della scheda, nell'ordine in cui la legge le vuole leggibili.
 *
 *  `href` costruisce il collegamento dal valore stesso: separare il testo
 *  mostrato dall'indirizzo di destinazione è il modo in cui un numero cambia
 *  nella pagina e resta vecchio nel link. */
const FIELDS: {
  key: string;
  href?: (value: string) => string;
  external?: boolean;
}[] = [
  { key: "directors" },
  { key: "phone", href: (v) => `tel:${dialable(v)}` },
  { key: "email", href: (v) => `mailto:${v}` },
  { key: "website", href: (v) => `https://${v.replace(/^https?:\/\//, "")}`, external: true },
  { key: "vatId" },
  { key: "court" },
  { key: "registerNumber" },
];

export function Impressum() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo(0, 0);
    const previous = document.title;
    document.title = `${t("impressum.title")} · ${cfg.brand_name}`;
    return () => { document.title = previous; };
  }, [t, cfg.brand_name]);

  return (
    <div style={{ background: "var(--color-light-bg)", minHeight: "100vh" }}>
      <style>{IMPRESSUM_CSS}</style>
      <div
        style={{
          maxWidth: 780,
          margin: "0 auto",
          padding: isMobile ? "88px 24px 72px" : "128px 48px 96px",
        }}
      >
        <p className="im-overline">{t("impressum.overline")}</p>

        <div className="im-body">
          <h1>{t("impressum.title")}</h1>
          <p className="im-sub">{t("impressum.subtitle")}</p>

          {/* Ragione sociale e sede: la legge le vuole per prime e per esteso,
              non spezzate in etichette. */}
          <p className="im-company">{t("impressum.company")}</p>
          <p className="im-address">{t("impressum.address")}</p>

          <div>
            {FIELDS.map(({ key, href, external }) => {
              const value = t(`impressum.fields.${key}.value`);
              return (
                <div className="im-field" key={key}>
                  <div className="im-label">{t(`impressum.fields.${key}.label`)}</div>
                  <p className="im-value">
                    {href ? (
                      <a
                        href={href(value)}
                        {...(external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {value}
                      </a>
                    ) : (
                      value
                    )}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Esclusione di responsabilità sui contenuti collegati: chiude la
              scheda societaria, come chiede la prassi tedesca dell'Impressum. */}
          <section className="im-disclaimer">
            <h2>{t("impressum.disclaimer.title")}</h2>
            <p>{t("impressum.disclaimer.body")}</p>
          </section>

          <Link className="im-back" to={`/${locale ?? "it"}`}>
            {t("impressum.back")}
          </Link>
          {" · "}
          <Link className="im-back" to={pathFor("privacy", locale)} style={{ marginLeft: 0 }}>
            {t("footer.privacy")}
          </Link>
        </div>
      </div>
    </div>
  );
}
